import { ChevronLeft, RefreshCw, LayoutDashboard, BarChart3, Package, CalendarCheck, Target, Users } from 'lucide-react';
import PanelDateControls from './PanelDateControls';
import BrandLogo from './BrandLogo';
import './PanelHeader.css';

// El tab Catálogo (stock) solo aplica a marcas ecommerce. Daily/Leads solo donde
// se provee la query. `showCampaigns=false` oculta "Campañas y anuncios" (vista cliente).
function sectionsFor(vertical, showDaily, showLeads, showCampaigns, showCustomers) {
  // lead_gen: el tab "Leads" es el principal (el "Resumen" quedó viejo, no se muestra).
  const sections = showLeads
    ? [{ key: 'leads', label: 'Leads', Icon: Target }]
    : [{ key: 'summary', label: 'Resumen', Icon: LayoutDashboard }];
  if (showDaily) sections.push({ key: 'daily', label: 'Daily', Icon: CalendarCheck });
  if (showCampaigns) sections.push({ key: 'campaigns', label: 'Campañas y anuncios', Icon: BarChart3 });
  if (vertical === 'ecommerce' && showCustomers) sections.push({ key: 'customers', label: 'Clientes', Icon: Users });
  if (vertical === 'ecommerce') sections.push({ key: 'catalog', label: 'Catálogo', Icon: Package });
  return sections;
}

const VERTICAL_LABEL = { ecommerce: 'Ecommerce', lead_gen: 'Lead gen' };

function updatedTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' });
}

function SegmentedNav({ sections, section, onSectionChange }) {
  return (
    <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-1">
      {sections.map(({ key, label, Icon }) => {
        const active = section === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSectionChange(key)}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm transition-colors ${
              active ? 'bg-white font-medium text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Encabezado del panel de una marca. Admin: dos filas (breadcrumb + fechas /
 * identidad + segmentado). Cliente (`glass`): una barra superior flotante con
 * efecto glass — logo de la marca a la izquierda, navegación de fechas a la
 * derecha (y el segmentado solo si hay más de una sección).
 */
export default function PanelHeader({
  brand,
  updatedAt,
  dateControls,
  section,
  onSectionChange,
  onBack,
  brandSwitcher,
  showDaily = false,
  showLeads = false,
  showCampaigns = true,
  showCustomers = false,
  glass = false,
}) {
  const time = updatedTime(updatedAt);
  const sections = sectionsFor(brand?.vertical, showDaily, showLeads, showCampaigns, showCustomers);

  if (glass) {
    // Admin (hay brandSwitcher): barra fina a todo el ancho — logo espor.ai +
    // volver + un solo dropdown de marca + tabs + fechas, en una sola línea.
    if (brandSwitcher) {
      return (
        <div className="phglass phglass-admin">
          <span className="phglass-logo" aria-label="espor.ai">
            <svg width="24" height="24" viewBox="0 0 174 175" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M134.26 37.04C134.26 90.99 90.37 134.89 36.41 134.89V174.46C112.18 174.46 173.82 112.82 173.82 37.05H134.26V37.04Z" fill="#2f3df7" />
              <path d="M19.63 39.26C30.47 39.26 39.26 30.47 39.26 19.63C39.26 8.79 30.47 0 19.63 0C8.79 0 0 8.79 0 19.63C0 30.47 8.79 39.26 19.63 39.26Z" fill="#2f3df7" />
              <path d="M86.12 39.26C96.96 39.26 105.75 30.47 105.75 19.63C105.75 8.79 96.96 0 86.12 0C75.28 0 66.42 8.79 66.49 19.63C66.49 30.47 75.28 39.26 86.12 39.26Z" fill="#2f3df7" />
            </svg>
          </span>
          {onBack && (
            <button type="button" onClick={onBack} className="phglass-back" aria-label="Volver a marcas">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              <span className="phglass-back-lbl">Marcas</span>
            </button>
          )}
          <div className="phglass-switch">{brandSwitcher}</div>
          {sections.length > 1 && (
            <SegmentedNav sections={sections} section={section} onSectionChange={onSectionChange} />
          )}
          <div className="phglass-dates">
            <PanelDateControls controls={dateControls} showCompare />
          </div>
        </div>
      );
    }

    // Cliente/GP: identidad de la marca + estado de sync + tabs + fechas.
    return (
      <div className="phglass">
        <div className="phglass-brand">
          <BrandLogo name={brand?.name} logoUrl={brand?.logo_url} size="lg" />
          <div className="phglass-id">
            <span className="phglass-name">{brand?.name || 'Panel'}</span>
            <span className="phglass-sync">
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              Sincronizado 2×/día{time ? ` · última ${time}` : ''}
            </span>
          </div>
        </div>
        {sections.length > 1 && (
          <SegmentedNav sections={sections} section={section} onSectionChange={onSectionChange} />
        )}
        <div className="phglass-dates">
          <PanelDateControls controls={dateControls} showCompare />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Marcas
          </button>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap items-center gap-2">
          {brandSwitcher}
          <PanelDateControls controls={dateControls} showCompare />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <BrandLogo name={brand?.name} logoUrl={brand?.logo_url} size="lg" />
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold text-gray-900">{brand?.name || 'Marca'}</h1>
              {brand?.vertical && (
                <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                  {VERTICAL_LABEL[brand.vertical] ?? brand.vertical}
                </span>
              )}
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              Sincronizado 2×/día{time ? ` · última ${time}` : ''}
            </span>
          </div>
        </div>

        <SegmentedNav sections={sections} section={section} onSectionChange={onSectionChange} />
      </div>
    </div>
  );
}
